// Auth page redesign (Niranjan, Feb 2026) — Cormorant Garamond /
// DM Sans typography, sage-green primary (#4a7c59), warm-cream
// left panel (#f5f2ec), inline icons in inputs, password-visibility
// toggle, "Keep me signed in" checkbox, SSO button, and a
// content-rich right panel layered over the existing background
// image. Functional flows (login / register / forgot / reset) are
// preserved verbatim — only presentation changes.

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, API, formatApiErrorDetail } from '@/App';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Mail, Lock, Eye, EyeOff, Briefcase, ArrowRight,
  Check, User as UserIcon, Wheat, Sprout, FileText, Sparkles,
} from 'lucide-react';

const SAGE = '#4a7c59';
const CREAM = '#FAF6EE';            // warm cream (continuous across both halves)
const INK = '#1F3A2E';              // deep forest green — headlines + accents
const CHARCOAL = '#2C2C2C';         // soft charcoal — body
const SAGE_MUTED = '#7A8B7A';       // sage-grey — taglines + signature
const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY = "'Inter', 'DM Sans', system-ui, sans-serif";

// Reusable input field with leading icon + optional trailing slot
function IconInput({
  id, type, value, onChange, placeholder, leftIcon: LeftIcon,
  rightSlot, testid, required = true, autoComplete,
}) {
  return (
    <div className="relative">
      {LeftIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <LeftIcon size={16} style={{ color: '#8A8580' }} />
        </div>
      )}
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white outline-none transition-all"
        style={{
          fontFamily: FONT_BODY,
          fontSize: '14px',
          color: '#2C2A28',
          border: '1px solid rgba(74,124,89,0.18)',
          borderRadius: '10px',
          padding: '12px 14px',
          paddingLeft: LeftIcon ? '42px' : '14px',
          paddingRight: rightSlot ? '42px' : '14px',
        }}
        onFocus={e => { e.target.style.boxShadow = '0 0 0 3px rgba(74,124,89,0.10)'; e.target.style.borderColor = SAGE; }}
        onBlur={e => { e.target.style.boxShadow = 'none'; e.target.style.borderColor = 'rgba(74,124,89,0.18)'; }}
      />
      {rightSlot && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {rightSlot}
        </div>
      )}
    </div>
  );
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block mb-1.5"
      style={{
        fontFamily: FONT_BODY,
        fontSize: '12px',
        fontWeight: 500,
        color: '#5E5A56',
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </label>
  );
}

// Right-panel feature row — outline icon on the left, title + body on the right.
function FeatureRow({ icon: Icon, title, body }) {
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0" style={{ color: INK, marginTop: 2 }} aria-hidden>
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div>
        <div
          style={{
            fontFamily: FONT_BODY,
            fontSize: 14,
            fontWeight: 500,
            color: INK,
            marginBottom: 4,
            letterSpacing: '0.005em',
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontFamily: FONT_BODY,
            fontSize: 13,
            fontWeight: 300,
            color: CHARCOAL,
            lineHeight: 1.6,
          }}
        >
          {body}
        </div>
      </div>
    </div>
  );
}

function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, login, register } = useAuth();

  const isLogin = location.pathname === '/login';
  const isForgotPassword = location.pathname === '/forgot-password';
  const isResetPassword = location.pathname === '/reset-password';

  const [formData, setFormData] = useState({
    email: '', password: '', name: '',
    newPassword: '', confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isForgotPassword) {
        await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
        setResetMessage('Password reset link has been sent to your email (check console in development)');
        toast.success('Reset link sent!');
      } else if (isResetPassword) {
        const token = searchParams.get('token');
        if (formData.newPassword !== formData.confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        await axios.post(`${API}/auth/reset-password`, { token, new_password: formData.newPassword });
        toast.success('Password reset successful!');
        navigate('/login');
      } else if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          toast.success('Welcome back!');
          navigate('/dashboard');
        } else {
          setError(result.error);
        }
      } else {
        const result = await register(formData.email, formData.password, formData.name);
        if (result.success) {
          toast.success('Account created successfully!');
          navigate('/dashboard');
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ── Emergent-managed Google Auth ──
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Heading + subtitle vary by mode but use the same typography
  const headingText = isForgotPassword
    ? 'Reset access.'
    : isResetPassword
      ? 'Set a new password.'
      : isLogin
        ? 'Welcome back.'
        : 'Join the lab.';
  const subtitleText = isForgotPassword
    ? 'Enter your registered email — we will send a secure reset link valid for the next 30 minutes.'
    : isResetPassword
      ? 'Choose a strong password. You will be signed in automatically once the reset completes.'
      : isLogin
        ? 'Statistical Analysis Platform for Agricultural Research. Sign in to continue your work.'
        : 'Statistical Analysis Platform for Agricultural Research. Create an account to start your first trial.';

  // Eye / eye-off button used as the trailing slot for password fields
  const eyeButton = (visible, onToggle, testid) => (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="flex items-center justify-center rounded-md transition-colors"
      style={{ width: 28, height: 28, color: '#8A8580' }}
      onMouseEnter={e => (e.currentTarget.style.color = SAGE)}
      onMouseLeave={e => (e.currentTarget.style.color = '#8A8580')}
      aria-label={visible ? 'Hide password' : 'Show password'}
    >
      {visible ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  );

  return (
    <div
      className="min-h-screen flex"
      data-testid="auth-page"
      style={{ fontFamily: FONT_BODY, color: '#2C2A28' }}
    >
      {/* ──────────────── LEFT PANEL — FORM ──────────────── */}
      <div
        className="w-full lg:w-1/2 flex flex-col"
        style={{ background: CREAM }}
      >
        <div className="flex-1 flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            {/* Brand mark */}
            <div className="mb-8 flex items-center gap-2.5" data-testid="app-logo-title">
              <div
                className="flex items-center justify-center"
                style={{
                  width: 30, height: 30,
                  borderRadius: '8px',
                  background: SAGE,
                  color: 'white',
                  fontFamily: FONT_HEADING,
                  fontWeight: 600,
                  fontSize: 18,
                  letterSpacing: '0.02em',
                }}
              >
                B
              </div>
              <span
                style={{
                  fontFamily: FONT_BODY,
                  fontWeight: 500,
                  fontSize: 15,
                  color: '#2C2A28',
                  letterSpacing: '0.02em',
                }}
              >
                BKAgriD
              </span>
            </div>

            {/* Headline + subtitle */}
            <div className="mb-10">
              <h1
                style={{
                  fontFamily: FONT_HEADING,
                  fontSize: '42px',
                  lineHeight: 1.05,
                  fontWeight: 500,
                  color: '#2C2A28',
                  letterSpacing: '-0.01em',
                  marginBottom: 14,
                }}
              >
                {headingText}
              </h1>
              <p
                style={{
                  fontFamily: FONT_BODY,
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: '#7A7570',
                  fontWeight: 400,
                  maxWidth: 380,
                }}
              >
                {subtitleText}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isResetPassword && (
                <div>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <IconInput
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@institute.edu"
                    leftIcon={Mail}
                    testid="email-input"
                    autoComplete="email"
                  />
                </div>
              )}

              {!isForgotPassword && !isResetPassword && (
                <div>
                  <FieldLabel htmlFor="password">Password</FieldLabel>
                  <IconInput
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    leftIcon={Lock}
                    rightSlot={eyeButton(showPassword, () => setShowPassword(p => !p), 'password-visibility-toggle')}
                    testid="password-input"
                    autoComplete={isLogin ? 'current-password' : 'new-password'}
                  />
                </div>
              )}

              {isResetPassword && (
                <>
                  <div>
                    <FieldLabel htmlFor="newPassword">New password</FieldLabel>
                    <IconInput
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                      placeholder="••••••••"
                      leftIcon={Lock}
                      rightSlot={eyeButton(showNewPassword, () => setShowNewPassword(p => !p), 'new-password-visibility-toggle')}
                      testid="new-password-input"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="confirmPassword">Confirm password</FieldLabel>
                    <IconInput
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      leftIcon={Lock}
                      rightSlot={eyeButton(showConfirmPassword, () => setShowConfirmPassword(p => !p), 'confirm-password-visibility-toggle')}
                      testid="confirm-password-input"
                      autoComplete="new-password"
                    />
                  </div>
                </>
              )}

              {!isLogin && !isForgotPassword && !isResetPassword && (
                <div>
                  <FieldLabel htmlFor="name">Full name</FieldLabel>
                  <IconInput
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Dr. Jane Doe"
                    leftIcon={UserIcon}
                    testid="name-input"
                    autoComplete="name"
                  />
                </div>
              )}

              {/* Keep-signed-in checkbox + Forgot link — login only */}
              {isLogin && (
                <div className="flex items-center justify-between pt-1">
                  <label
                    className="flex items-center gap-2 cursor-pointer select-none"
                    data-testid="keep-signed-in-label"
                  >
                    <span
                      className="flex items-center justify-center transition-all"
                      style={{
                        width: 16, height: 16,
                        borderRadius: 4,
                        border: keepSignedIn ? `1px solid ${SAGE}` : '1px solid rgba(74,124,89,0.30)',
                        background: keepSignedIn ? SAGE : 'white',
                      }}
                    >
                      {keepSignedIn && <Check size={11} color="white" strokeWidth={3} />}
                    </span>
                    <input
                      type="checkbox"
                      checked={keepSignedIn}
                      onChange={e => setKeepSignedIn(e.target.checked)}
                      className="sr-only"
                      data-testid="keep-signed-in-checkbox"
                    />
                    <span style={{ fontSize: '13px', color: '#5E5A56' }}>
                      Keep me signed in for 30 days
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    data-testid="forgot-password-link"
                    style={{ fontSize: '13px', color: SAGE, fontWeight: 500 }}
                    className="hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {error && (
                <div
                  data-testid="error-message"
                  style={{
                    background: '#FBE9E3',
                    border: '1px solid #C87A5E',
                    borderRadius: 10,
                    padding: '10px 14px',
                    color: '#9F4124',
                    fontSize: 13,
                    fontFamily: FONT_BODY,
                  }}
                >
                  {error}
                </div>
              )}
              {resetMessage && (
                <div
                  data-testid="success-message"
                  style={{
                    background: '#E6F0E9',
                    border: `1px solid ${SAGE}`,
                    borderRadius: 10,
                    padding: '10px 14px',
                    color: '#2F5740',
                    fontSize: 13,
                    fontFamily: FONT_BODY,
                  }}
                >
                  {resetMessage}
                </div>
              )}

              {/* Primary submit */}
              <button
                type="submit"
                disabled={loading}
                data-testid="submit-button"
                className="w-full transition-all active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: SAGE,
                  color: 'white',
                  borderRadius: '10px',
                  padding: '13px 20px',
                  fontFamily: FONT_BODY,
                  fontSize: '14px',
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                  boxShadow: '0 1px 2px rgba(74,124,89,0.20)',
                  marginTop: 8,
                }}
                onMouseEnter={e => !loading && (e.currentTarget.style.background = '#3F6B4C')}
                onMouseLeave={e => (e.currentTarget.style.background = SAGE)}
              >
                {loading
                  ? 'Processing…'
                  : isForgotPassword ? 'Send reset link'
                    : isResetPassword ? 'Reset password'
                      : isLogin ? 'Sign in'
                        : 'Create account'}
              </button>

              {/* "or continue with" divider — login + register only */}
              {(isLogin || (!isForgotPassword && !isResetPassword && !isLogin)) && (
                <>
                  <div className="flex items-center gap-3 pt-3" data-testid="auth-divider">
                    <span style={{ flex: 1, height: 1, background: 'rgba(74,124,89,0.18)' }} />
                    <span style={{ fontSize: '11px', color: '#8A8580', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      or continue with
                    </span>
                    <span style={{ flex: 1, height: 1, background: 'rgba(74,124,89,0.18)' }} />
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    data-testid="google-signin-button"
                    className="w-full flex items-center justify-center gap-3 transition-all hover:bg-[#fafafa]"
                    style={{
                      background: 'white',
                      color: '#2C2A28',
                      border: '1px solid rgba(74,124,89,0.25)',
                      borderRadius: '10px',
                      padding: '12px 20px',
                      fontFamily: FONT_BODY,
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
                      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
                      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <button
                    type="button"
                    onClick={() => toast.info('Institution SSO coming soon — contact your IT administrator to enable it for your institute.')}
                    data-testid="sso-button"
                    className="w-full flex items-center justify-center gap-2 transition-all hover:bg-[#fafafa]"
                    style={{
                      background: 'white',
                      color: '#2C2A28',
                      border: '1px solid rgba(74,124,89,0.25)',
                      borderRadius: '10px',
                      padding: '12px 20px',
                      fontFamily: FONT_BODY,
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    <Briefcase size={16} style={{ color: SAGE }} />
                    Sign in with Institution SSO
                  </button>
                </>
              )}
            </form>

            {/* Sign-up / Sign-in toggle line */}
            <div className="mt-5 text-center" style={{ fontSize: '13px', color: '#5E5A56' }}>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => navigate('/register')}
                  data-testid="toggle-auth-mode"
                  style={{ color: SAGE, fontWeight: 500 }}
                  className="inline-flex items-center gap-1 hover:underline"
                >
                  New researcher? Request access <ArrowRight size={13} />
                </button>
              )}
              {!isLogin && (
                <span>
                  {isForgotPassword || isResetPassword ? 'Remember your password? ' : 'Already have an account? '}
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    data-testid="toggle-auth-mode"
                    style={{ color: SAGE, fontWeight: 500 }}
                    className="hover:underline"
                  >
                    Sign in
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-8 lg:px-16 pb-6 flex flex-col gap-2"
          data-testid="auth-footer"
        >
          <button
            type="button"
            onClick={() => navigate('/about')}
            className="self-start inline-flex items-center gap-1 transition-colors"
            style={{
              fontFamily: FONT_HEADING,
              fontStyle: 'italic',
              fontSize: 13,
              color: SAGE_MUTED,
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              letterSpacing: '0.01em',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = SAGE; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = SAGE_MUTED; }}
            data-testid="auth-footer-about-link"
          >
            Learn about the team <ArrowRight size={12} />
          </button>
          <div style={{ fontSize: '11px', color: '#A09B95', fontFamily: FONT_BODY, letterSpacing: '0.02em' }}>
            © 2025 BKAgriD · <span className="hover:text-[#5E5A56] cursor-pointer">Privacy</span> ·{' '}
            <span className="hover:text-[#5E5A56] cursor-pointer">Terms</span> ·{' '}
            <span className="hover:text-[#5E5A56] cursor-pointer">Support</span>
          </div>
        </div>
      </div>

      {/* ──────────────── RIGHT PANEL — JOURNAL HERO ──────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 relative items-center"
        style={{ background: CREAM }}
        data-testid="auth-hero"
      >
        {/* Subtle 1px sage hairline divider between halves */}
        <div
          className="absolute left-0 top-0 bottom-0"
          style={{ width: 1, background: 'rgba(122,139,122,0.25)' }}
          aria-hidden
        />

        {/* Botanical watermark — single wheat stalk, 6% opacity, bottom-right */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: 24,
            right: 12,
            opacity: 0.06,
            color: INK,
            transform: 'rotate(-12deg)',
          }}
          aria-hidden
        >
          <Wheat size={420} strokeWidth={1} />
        </div>

        {/* Centered content column, max 480px */}
        <div
          className="relative z-10 px-12 xl:px-20 w-full"
          style={{ maxWidth: 560, margin: '0 auto' }}
        >
          {/* Tagline — sage, uppercase, letter-spaced */}
          <div
            data-testid="hero-tagline"
            style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: SAGE_MUTED,
              marginBottom: 28,
            }}
          >
            BKAgriD · Agricultural Research Platform
          </div>

          {/* Main headline — Cormorant italic, deep forest green */}
          <h2
            data-testid="hero-headline"
            style={{
              fontFamily: FONT_HEADING,
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: 48,
              lineHeight: 1.08,
              color: INK,
              letterSpacing: '-0.012em',
              marginBottom: 22,
              maxWidth: 480,
            }}
          >
            For agriculture, you don&rsquo;t need to go anywhere else.
          </h2>

          {/* Subheading */}
          <p
            style={{
              fontFamily: FONT_BODY,
              fontSize: 16,
              fontWeight: 300,
              color: CHARCOAL,
              lineHeight: 1.6,
              maxWidth: 480,
              marginBottom: 32,
            }}
          >
            One workspace. Every analysis. From field trial to peer-reviewed publication — with results and figures journals accept.
          </p>

          {/* Capability pills — separated by · */}
          <div
            data-testid="hero-pills"
            className="flex flex-wrap items-center"
            style={{ gap: '8px', marginBottom: 36, maxWidth: 480 }}
          >
            {[
              'Experimental Design',
              'Biometrical Genetics',
              'Molecular Analysis',
              'BK Intelligence',
            ].map((label, i, arr) => (
              <React.Fragment key={label}>
                <span
                  style={{
                    fontFamily: FONT_BODY,
                    fontSize: 11,
                    fontWeight: 500,
                    color: INK,
                    border: `1px solid ${INK}`,
                    background: 'transparent',
                    borderRadius: 999,
                    padding: '5px 12px',
                    letterSpacing: '0.02em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </span>
                {i < arr.length - 1 && (
                  <span style={{ color: SAGE_MUTED, fontWeight: 300, fontSize: 13 }}>·</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Feature highlights — 3 only */}
          <div
            data-testid="hero-features"
            className="flex flex-col"
            style={{ gap: 22, maxWidth: 480 }}
          >
            <FeatureRow
              icon={Sprout}
              title={'Design \u2192 Analyze \u2192 Publish'}
              body={'RCBD, CRD, Factorial, Split-plot, AMMI, GGE biplot, GWAS, Genomic Selection \u2014 all in one flow.'}
            />
            <FeatureRow
              icon={FileText}
              title="Publication-Ready Outputs"
              body="High-resolution figures, formatted ANOVA tables, and DOCX/PDF reports ready for journal submission."
            />
            <FeatureRow
              icon={Sparkles}
              title="BK Intelligence"
              body={'AI research assistant that drafts manuscripts, interprets results, and answers technical questions \u2014 trained on agricultural science.'}
            />
          </div>

          {/* Bottom signature — Cormorant italic, sage */}
          <div
            data-testid="hero-signature"
            style={{
              marginTop: 40,
              fontFamily: FONT_HEADING,
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: 13,
              color: SAGE_MUTED,
              letterSpacing: '0.01em',
            }}
          >
            Built for researchers, breeders, and scholars who value rigor.
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
